import React, { useEffect, useRef, useState } from "react";
import { Flame, Dumbbell, Apple, Droplets, Moon, Sparkles, Check } from "lucide-react";
import { ProgressRing, Bar } from "../app/ui";
import api from "../../lib/api";

const NAV = [Sparkles, Flame, Dumbbell, Apple, Moon];

function useCountUp(target, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!target) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

// Floating glass card that overlays the hero window with REAL live platform stats.
export function HeroLiveBadge() {
  const [stats, setStats] = useState({ members: 0, countries: 0, checkins: 0 });
  useEffect(() => {
    const load = () => api.publicStats().then(setStats).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);
  const members = useCountUp(stats.members);
  const countries = useCountUp(stats.countries);
  return (
    <div
      data-testid="hero-live-badge"
      className="glass flex items-center gap-4 rounded-2xl px-4 py-3 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.8)]"
    >
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-mist-muted">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sage opacity-70" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-sage" />
        </span>
        Live
      </div>
      <div className="flex items-center gap-4">
        <div>
          <div className="font-display text-lg font-bold leading-none text-mist">{members.toLocaleString()}</div>
          <div className="mt-0.5 text-[10px] uppercase tracking-wider text-mist-muted">Members</div>
        </div>
        <div className="h-7 w-px bg-white/10" />
        <div>
          <div className="font-display text-lg font-bold leading-none text-mist">{countries}</div>
          <div className="mt-0.5 text-[10px] uppercase tracking-wider text-mist-muted">Countries</div>
        </div>
      </div>
    </div>
  );
}

// A premium, Linear-style product window used as the hero showpiece.
// Pure markup (no data fetch) — a faithful, calm preview of the real dashboard.
// Cycling social-proof ticker — privacy-safe (city + country only, never names).
export function HeroJoinTicker() {
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);
  useEffect(() => {
    api.publicRecent().then((r) => setItems(Array.isArray(r?.recent) ? r.recent : [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setI((v) => (v + 1) % items.length), 3600);
    return () => clearInterval(t);
  }, [items.length]);
  if (!items.length) return null;
  const it = items[i];
  const place = it.city ? `${it.city}, ${it.country}` : it.country;
  return (
    <div
      data-testid="hero-join-ticker"
      className="glass flex items-center gap-2.5 rounded-full px-4 py-2.5 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.8)]"
    >
      <span className="text-base leading-none">{it.flag}</span>
      <span key={i} className="animate-fade-in text-xs text-mist">
        <span className="text-mist-muted">Someone from</span> <span className="font-semibold">{place}</span> <span className="text-mist-muted">just joined</span>
      </span>
    </div>
  );
}

export function HeroWindow() {
  return (
    <div className="relative w-full overflow-hidden rounded-[1.4rem] border border-white/[0.09] bg-ink-surface/90 shadow-[0_50px_140px_-30px_rgba(0,0,0,0.85)] backdrop-blur-xl">
      {/* window chrome */}
      <div className="flex items-center gap-2 border-b border-white/[0.06] px-5 py-3">
        <span className="h-3 w-3 rounded-full bg-[#ff5f57]/70" />
        <span className="h-3 w-3 rounded-full bg-[#febc2e]/70" />
        <span className="h-3 w-3 rounded-full bg-[#28c840]/70" />
        <div className="mx-auto flex items-center gap-2 rounded-full border border-white/[0.06] bg-white/[0.03] px-4 py-1 text-[11px] text-mist-muted">
          <span className="h-1.5 w-1.5 rounded-full bg-sage" /> getwyvn.com/dashboard
        </div>
      </div>

      <div className="flex">
        {/* left rail */}
        <div className="hidden w-14 flex-col items-center gap-4 border-r border-white/[0.06] py-5 sm:flex">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-brand/15 font-display text-sm font-bold text-brand-soft">W</div>
          {NAV.map((Icon, i) => (
            <span key={i} className={`flex h-9 w-9 items-center justify-center rounded-xl ${i === 0 ? "bg-white/[0.06] text-mist" : "text-mist-muted"}`}>
              <Icon size={16} />
            </span>
          ))}
        </div>

        {/* main */}
        <div className="flex-1 p-5 sm:p-7">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-brand-soft">Monday · Today</div>
              <div className="mt-1 font-display text-xl font-semibold text-mist sm:text-2xl">Good morning, Amara</div>
            </div>
            <div className="hidden items-center gap-2 rounded-full border border-white/[0.06] bg-white/[0.02] px-3 py-1.5 sm:flex">
              <Flame size={14} className="text-gold" />
              <span className="text-sm font-semibold text-mist">23</span>
              <span className="text-xs text-mist-muted">day streak</span>
            </div>
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            {/* ring card */}
            <div className="flex items-center gap-5 rounded-2xl border border-white/[0.07] bg-white/[0.02] p-5">
              <ProgressRing value={1840} max={2500} size={104} stroke={9}>
                <span className="font-display text-lg font-bold text-mist">74%</span>
                <span className="text-[9px] text-mist-muted">today</span>
              </ProgressRing>
              <div className="flex-1">
                <div className="text-sm font-semibold text-mist">Daily wins</div>
                <div className="mt-3 space-y-2">
                  {[["Morning workout", true], ["Log breakfast", true], ["2L water", false]].map(([t, done]) => (
                    <div key={t} className="flex items-center gap-2 text-xs">
                      <span className={`flex h-4 w-4 items-center justify-center rounded-full ${done ? "bg-sage/20 text-sage" : "border border-white/15 text-transparent"}`}>
                        <Check size={11} />
                      </span>
                      <span className={done ? "text-mist-muted line-through" : "text-mist"}>{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* macro card */}
            <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-mist">Nutrition</span>
                <span className="text-xs text-mist-muted">1,620 / 2,100 kcal</span>
              </div>
              <div className="mt-4 space-y-3">
                {[["Protein", 96, 130, "#6AA6FF"], ["Carbs", 180, 260, "#7A9E7E"], ["Fat", 52, 70, "#D4924A"]].map(([l, v, m, c]) => (
                  <div key={l}>
                    <div className="mb-1 flex justify-between text-[11px]"><span className="text-mist-muted">{l}</span><span className="text-mist">{v}g</span></div>
                    <Bar value={v} max={m} color={c} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* stat tiles */}
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { icon: Dumbbell, label: "Workouts", value: "5", color: "#6AA6FF" },
              { icon: Flame, label: "Calories", value: "3.2k", color: "#D4924A" },
              { icon: Droplets, label: "Water", value: "1.4L", color: "#7DB2FF" },
              { icon: Moon, label: "Sleep", value: "7h 40", color: "#A9CCFF" },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-3.5">
                <s.icon size={16} style={{ color: s.color }} />
                <div className="mt-2 font-display text-lg font-bold text-mist">{s.value}</div>
                <div className="text-[10px] uppercase tracking-wider text-mist-muted">{s.label}</div>
              </div>
            ))}
          </div>

          {/* coach nudge */}
          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-brand/25 bg-brand/[0.06] p-4">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/20 font-display text-sm font-bold text-brand-soft">A</span>
            <p className="text-xs leading-relaxed text-mist">
              <span className="font-semibold text-mist">Amara:</span> Beautiful start. One more glass of water and you close today's ring — you've got this. 💧
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
